import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api/client";
import type { LobbyStatusResponse } from "../api/types";
import { setPendingLobby, type ActiveLobby } from "../link/activeLobby";
import { linkText } from "../strings";
import { useWallet } from "../wallet/WalletProvider";

const POLL_MS = 2_000;

function errorText(e: unknown): string {
  if (e && typeof e === "object" && "body" in e) {
    const body = (e as { body: { message?: string; reason?: string } }).body;
    return body.message ?? body.reason ?? "Request failed";
  }
  return e instanceof Error ? e.message : String(e);
}

/**
 * Create or join a Cable Club lobby (WEB-20). `onEnter` hands the lobby to the game screen, which
 * waits for the forced save at the Cable Club desk, binds it and runs the link.
 */
export function LobbySetup(props: { enterLabel: string; onEnter: (lobby: ActiveLobby) => void }) {
  const [mode, setMode] = useState<"trade" | "battle" | "wager_battle">("trade");
  const [serverRun, setServerRun] = useState(false);
  const [lobby, setLobby] = useState<ActiveLobby | null>(null);
  const [status, setStatus] = useState<LobbyStatusResponse | null>(null);
  const [joinCode, setJoinCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  const lobbyId = lobby?.lobbyId ?? null;
  useEffect(() => {
    if (!lobbyId) return;
    let stopped = false;
    const poll = async () => {
      try {
        const s = await api.lobby(lobbyId);
        if (!stopped) setStatus(s);
      } catch (e) {
        if (!stopped) setError(errorText(e));
      }
    };
    void poll();
    const t = setInterval(() => void poll(), POLL_MS);
    return () => {
      stopped = true;
      clearInterval(t);
    };
  }, [lobbyId]);

  const create = async () => {
    setError(null);
    try {
      const res = await api.createLobby(mode, mode === "wager_battle" || serverRun);
      setLobby({ lobbyId: res.lobbyId, code: res.code, hostMode: res.hostMode, mode });
    } catch (e) {
      setError(errorText(e));
    }
  };

  const join = async () => {
    setError(null);
    const code = joinCode.trim().toLowerCase();
    try {
      const res = await api.joinLobby(code);
      setLobby({ lobbyId: res.lobbyId, code, hostMode: res.hostMode, mode: res.mode });
    } catch (e) {
      setError(errorText(e));
    }
  };

  return (
    <>
      {!lobby && (
        <>
          <section className="card">
            <h2>Create a lobby</h2>
            <select value={mode} onChange={(e) => setMode(e.target.value as typeof mode)}>
              <option value="trade">Trade</option>
              <option value="battle">Battle</option>
              <option value="wager_battle">Wager battle</option>
            </select>
            {mode !== "wager_battle" && (
              <label>
                <input type="checkbox" checked={serverRun} onChange={(e) => setServerRun(e.target.checked)} /> Run on the server
              </label>
            )}
            <button onClick={create}>Create</button>
          </section>
          <section className="card">
            <h2>Join a lobby</h2>
            <input placeholder="Code" value={joinCode} onChange={(e) => setJoinCode(e.target.value)} />
            <button disabled={!/^[0-9a-f]{8}$/i.test(joinCode.trim())} onClick={join}>
              Join
            </button>
          </section>
          <p className="hint small">{linkText.privacy}</p>
        </>
      )}
      {lobby && (
        <section className="card">
          <h2>Lobby</h2>
          {lobby.code && (
            <p>
              Share this code: <code className="big">{lobby.code.toUpperCase()}</code>
            </p>
          )}
          {status ? (
            <ul>
              <li>Mode: {status.mode}</li>
              <li>Hosting: {status.hostMode === "server" ? "server-run" : "peer lockstep"}</li>
              <li>Status: {status.status}</li>
              <li>You are side {status.side}</li>
              <li>{status.opponentJoined ? "Opponent joined" : "Waiting for an opponent…"}</li>
            </ul>
          ) : (
            <p>Loading…</p>
          )}
          <p className="hint">{linkText.armed}</p>
          <button disabled={status !== null && status.status !== "lobby"} onClick={() => props.onEnter(status ? { ...lobby, hostMode: status.hostMode } : lobby)}>
            {props.enterLabel}
          </button>
        </section>
      )}
      {error && <p className="status error">{error}</p>}
    </>
  );
}

/** /link — Cable Club lobby page; entering the lobby continues on /play. */
export function LinkLobby() {
  const { auth } = useWallet();
  const navigate = useNavigate();

  if (!auth)
    return (
      <main className="page">
        <Link to="/">Log in</Link> to use the Cable Club.
      </main>
    );

  return (
    <main className="page link">
      <h1>Cable Club</h1>
      <LobbySetup
        enterLabel="Go to the Cable Club"
        onEnter={(lobby) => {
          setPendingLobby(lobby);
          navigate("/play");
        }}
      />
    </main>
  );
}
